/**
 * CrmCompanyChip — small badge chip that links to /crm/companies/:id.
 * Handles matched / ambiguous / unmatched states.
 */

import { Link } from 'react-router-dom';
import { Building2, HelpCircle, Unlink, AlertCircle } from 'lucide-react';
import type { MatchStatus, MatchedCompany } from '../types';

interface CrmCompanyChipProps {
  matchStatus: MatchStatus | null;
  matchedCompany: MatchedCompany | null;
  /** Number of candidate companies when ambiguous */
  ambiguousCount?: number;
}

export function CrmCompanyChip({
  matchStatus,
  matchedCompany,
  ambiguousCount,
}: CrmCompanyChipProps) {
  if (matchStatus === 'matched' && matchedCompany) {
    return (
      <Link
        to={`/crm/companies/${matchedCompany.id}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2.5 py-0.5 text-xs font-medium text-green-800 hover:bg-green-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
      >
        <Building2 className="h-3 w-3 shrink-0" />
        {matchedCompany.name}
      </Link>
    );
  }

  // matched status but company has since been deleted — neutral, no link
  if (matchStatus === 'matched' && !matchedCompany) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
        <AlertCircle className="h-3 w-3 shrink-0" />
        CRM: company unavailable
      </span>
    );
  }

  if (matchStatus === 'ambiguous') {
    const label =
      ambiguousCount !== undefined && ambiguousCount > 0
        ? `CRM: unsure (${ambiguousCount} companies)`
        : 'CRM: unsure';
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-medium text-amber-800">
        <HelpCircle className="h-3 w-3 shrink-0" />
        {label}
      </span>
    );
  }

  // unmatched or null
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      <Unlink className="h-3 w-3 shrink-0" />
      CRM: unmatched
    </span>
  );
}
